import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Filter,
  Loader2,
  Calendar,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader, ConfirmDialog, SearchInput } from '../../components/ui';
import { Panel, Badge, Button, buttonClass, card } from '../../design-system';
import { formatCurrency, formatDate } from '../../utils/format';

interface Lancamento {
  id: number;
  descricao: string;
  valor: number;
  valorPago: number;
  dataVencimento: string;
  dataPagamento: string | null;
  status: string;
  contaNome: string | null;
  categoriaNome: string | null;
  categoriaCor: string | null;
  pessoaNome: string | null;
  parcelaAtual: number | null;
  totalParcelas: number | null;
}

interface Filtros {
  busca: string;
  status: string;
  categoriaId: string;
  dataInicio: string;
  dataFim: string;
}

export function ContasAReceber() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const [lancamentos, setLancamentos] = useState<Lancamento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtros, setFiltros] = useState<Filtros>({
    busca: '',
    status: 'PENDENTE',
    categoriaId: '',
    dataInicio: '',
    dataFim: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [confirmBaixa, setConfirmBaixa] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLancamentos = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: '20',
        tipo: 'RECEITA',
      });

      if (filtros.busca) params.append('busca', filtros.busca);
      if (filtros.status) params.append('status', filtros.status);
      if (filtros.categoriaId) params.append('categoriaId', filtros.categoriaId);
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim);

      const response = await api.get(`/financeiro/lancamentos?${params}`);
      setLancamentos(response.data.items);
      setTotalPages(response.data.totalPages);
    } catch {
      showError('Erro ao carregar lancamentos');
    } finally {
      setIsLoading(false);
    }
  }, [page, filtros, showError]);

  useEffect(() => {
    const controller = new AbortController();
    fetchLancamentos();
    return () => controller.abort();
  }, [fetchLancamentos]);

  const handleBaixar = async (id: number) => {
    try {
      await api.post(`/financeiro/lancamentos/${id}/baixar`, {
        dataPagamento: new Date().toISOString().split('T')[0],
      });
      showSuccess('Conta recebida com sucesso');
      fetchLancamentos();
    } catch {
      showError('Erro ao receber conta');
    }
    setConfirmBaixa(null);
  };

  const isVencido = (dataVencimento: string, status: string) => {
    if (status !== 'PENDENTE') return false;
    return new Date(dataVencimento) < new Date(new Date().toDateString());
  };

  const getStatusBadge = (status: string, dataVencimento: string) => {
    if (status === 'PAGO') {
      return <Badge tone="success">Recebido</Badge>;
    }
    if (status === 'CANCELADO') {
      return <Badge tone="neutral">Cancelado</Badge>;
    }
    if (isVencido(dataVencimento, status)) {
      return <Badge tone="danger">Vencido</Badge>;
    }
    return <Badge tone="warning">A receber</Badge>;
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Contas a Receber" subtitle="Gerencie suas receitas">
        <Link
          to="/financeiro/lancamentos/novo?tipo=RECEITA"
          className={buttonClass('primary')}
        >
          <Plus className="w-4 h-4" />
          Nova Receita
        </Link>
      </PageHeader>

      {/* Filtros */}
      <Panel title="Filtros" icon={<Filter className="h-4 w-4" />} bodyClassName="space-y-0">
        <div className="flex items-center gap-4 flex-wrap">
          <SearchInput
            value={filtros.busca}
            onChange={(value) => setFiltros({ ...filtros, busca: value })}
            placeholder="Buscar por descrição, cliente..."
          />
          <select
            value={filtros.status}
            onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
            className="px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todos os status</option>
            <option value="PENDENTE">A receber</option>
            <option value="PAGO">Recebido</option>
            <option value="CANCELADO">Cancelado</option>
          </select>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={buttonClass('secondary')}
          >
            <Filter className="w-4 h-4" />
            Filtros
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data Inicio</label>
              <input
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => setFiltros({ ...filtros, dataInicio: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data Fim</label>
              <input
                type="date"
                value={filtros.dataFim}
                onChange={(e) => setFiltros({ ...filtros, dataFim: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}
      </Panel>

      {/* Lista */}
      <div className={`${card} overflow-hidden`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : lancamentos.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-300" />
            <p>Nenhuma conta a receber encontrada</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Descricao</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Categoria</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Vencimento</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Valor</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Acoes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lancamentos.map((lancamento) => (
                <tr
                  key={lancamento.id}
                  className={`hover:bg-slate-50 cursor-pointer ${
                    isVencido(lancamento.dataVencimento, lancamento.status) ? 'bg-rose-50' : ''
                  }`}
                  onClick={() => navigate(`/financeiro/lancamentos/${lancamento.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {isVencido(lancamento.dataVencimento, lancamento.status) && (
                        <AlertTriangle className="w-4 h-4 text-rose-500" />
                      )}
                      <div>
                        <p className="font-semibold text-slate-900">{lancamento.descricao}</p>
                        {lancamento.parcelaAtual && (
                          <p className="text-xs text-slate-400">
                            Parcela {lancamento.parcelaAtual}/{lancamento.totalParcelas}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {lancamento.pessoaNome || '-'}
                  </td>
                  <td className="px-4 py-3">
                    {lancamento.categoriaNome && (
                      <span
                        className="px-2 py-1 text-xs rounded-full"
                        style={{
                          backgroundColor: `${lancamento.categoriaCor}20`,
                          color: lancamento.categoriaCor || '#6B7280',
                        }}
                      >
                        {lancamento.categoriaNome}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {formatDate(lancamento.dataVencimento)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-emerald-600">
                    {formatCurrency(lancamento.valor)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getStatusBadge(lancamento.status, lancamento.dataVencimento)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {lancamento.status === 'PENDENTE' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmBaixa(lancamento.id);
                        }}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Receber"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Paginacao */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Pagina {page} de {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Anterior
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Proximo
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmBaixa !== null}
        onClose={() => setConfirmBaixa(null)}
        onConfirm={() => confirmBaixa && handleBaixar(confirmBaixa)}
        title="Receber Conta"
        message="Deseja marcar esta conta como recebida?"
        confirmText="Receber"
        type="success"
      />
    </div>
  );
}
